import { useState, useMemo } from 'react';
import { Segmented, Skeleton } from 'antd';
import { CHART_COLORS, type DateFilterType } from '../utils/calculations';
import type { TieredQueryResult } from '../hooks/useTieredQueries';
import type { TopSatisStokMiktarRow, TopSatisStokTutarRow, KasaRaporuRow } from '../../../types';

interface PieChartProps {
  title: string;
  data: { name: string; value: number }[];
  isLoading?: boolean;
  valueFormatter?: (value: number) => string;
}

function PieChart({ title, data, isLoading, valueFormatter }: PieChartProps) {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const total = data.reduce((acc, item) => acc + item.value, 0);

  const slices = useMemo(() => {
    let currentAngle = -90;
    return data.map((item, index) => {
      const percentage = total > 0 ? (item.value / total) * 100 : 0;
      const angle = (percentage / 100) * 360;
      const startAngle = currentAngle;
      currentAngle += angle;
      return {
        ...item,
        percentage,
        startAngle,
        endAngle: currentAngle,
        color: CHART_COLORS[index % CHART_COLORS.length],
      };
    });
  }, [data, total]);

  const describeArc = (x: number, y: number, radius: number, startAngle: number, endAngle: number) => {
    const start = polarToCartesian(x, y, radius, endAngle);
    const end = polarToCartesian(x, y, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;
    return `M ${x} ${y} L ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y} Z`;
  };

  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees * Math.PI) / 180;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  };

  const formatValue = valueFormatter || ((v: number) => v.toLocaleString('tr-TR'));
  const hoveredSlice = hoveredIndex !== null ? slices[hoveredIndex] : null;

  return (
    <div style={chartStyles.container}>
      <div style={chartStyles.title}>{title}</div>

      {isLoading ? (
        <div style={chartStyles.loading}>
          <Skeleton.Avatar active size={180} shape="circle" />
          <div style={{ marginTop: 20, width: '100%' }}>
            <Skeleton active paragraph={{ rows: 4 }} title={false} />
          </div>
        </div>
      ) : data.length === 0 ? (
        <div style={chartStyles.empty}>Veri bulunamadı</div>
      ) : (
        <div style={chartStyles.content}>
          <div style={chartStyles.chartWrapper}>
            <svg viewBox="0 0 200 200" style={{ width: '100%', maxWidth: 220, height: 'auto', overflow: 'visible' }}>
              {slices.map((slice, index) => (
                <path
                  key={index}
                  d={describeArc(100, 100, hoveredIndex === index ? 85 : 80, slice.startAngle, slice.endAngle - 0.5)}
                  fill={slice.color}
                  stroke="#fff"
                  strokeWidth="1"
                  style={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: hoveredIndex !== null && hoveredIndex !== index ? 0.6 : 1,
                  }}
                  onMouseMove={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                />
              ))}
              <circle cx="100" cy="100" r="45" fill="var(--color-card-bg)" />
              {hoveredSlice ? (
                <>
                  <text x="100" y="92" textAnchor="middle" style={{ fontSize: 10, fill: 'var(--color-text-muted)', fontWeight: 500 }}>
                    {hoveredSlice.name.length > 12 ? hoveredSlice.name.slice(0, 12) + '...' : hoveredSlice.name}
                  </text>
                  <text x="100" y="107" textAnchor="middle" style={{ fontSize: 12, fill: 'var(--color-text-primary)', fontWeight: 700 }}>
                    {formatValue(hoveredSlice.value)}
                  </text>
                  <text x="100" y="120" textAnchor="middle" style={{ fontSize: 10, fill: hoveredSlice.color, fontWeight: 600 }}>
                    %{hoveredSlice.percentage.toFixed(1)}
                  </text>
                </>
              ) : (
                <>
                  <text x="100" y="95" textAnchor="middle" style={{ fontSize: 11, fill: 'var(--color-text-muted)', fontWeight: 500 }}>
                    Toplam
                  </text>
                  <text x="100" y="112" textAnchor="middle" style={{ fontSize: 13, fill: 'var(--color-text-primary)', fontWeight: 700 }}>
                    {formatValue(total)}
                  </text>
                </>
              )}
            </svg>
          </div>

          <div style={chartStyles.legend}>
            {slices.slice(0, 5).map((slice, index) => (
              <div
                key={index}
                style={{
                  ...chartStyles.legendItem,
                  opacity: hoveredIndex !== null && hoveredIndex !== index ? 0.5 : 1,
                  transform: hoveredIndex === index ? 'translateX(4px)' : 'none',
                  transition: 'all 0.2s ease',
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <div style={{ ...chartStyles.legendColor, backgroundColor: slice.color }} />
                <div style={chartStyles.legendText}>
                  <span style={chartStyles.legendLabel}>{slice.name}</span>
                  <span style={chartStyles.legendValue}>
                    {formatValue(slice.value)} ({slice.percentage.toFixed(1)}%)
                  </span>
                </div>
              </div>
            ))}
            {slices.length > 5 && (
              <div style={chartStyles.legendItem}>
                <div style={{ ...chartStyles.legendColor, backgroundColor: '#9ca3af' }} />
                <div style={chartStyles.legendText}>
                  <span style={chartStyles.legendLabel}>Diğer ({slices.length - 5} adet)</span>
                  <span style={chartStyles.legendValue}>
                    {formatValue(slices.slice(5).reduce((acc, s) => acc + s.value, 0))}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface ChartsSectionProps {
  queries: TieredQueryResult;
  dateFilter: DateFilterType;
  onDateFilterChange: (filter: DateFilterType) => void;
}

export function ChartsSection({ queries, dateFilter, onDateFilterChange }: ChartsSectionProps) {
  const { tier2Batch, getResult } = queries;

  const topSatisMiktarChartData = useMemo(() => {
    const rows = (getResult<TopSatisStokMiktarRow>('tier2', 'topSatisMiktar')?.data || []);
    return rows.slice(0, 10).map(row => ({
      name: row.MALINCINSI || row.STOKKODU || 'Bilinmeyen',
      value: row.TOPLAMCIKAN || 0,
    }));
  }, [tier2Batch.data]);

  const topSatisTutarChartData = useMemo(() => {
    const rows = (getResult<TopSatisStokTutarRow>('tier2', 'topSatisTutar')?.data || []);
    return rows.slice(0, 10).map(row => ({
      name: row.MALINCINSI || row.STOKKODU || 'Bilinmeyen',
      value: row.TOPLAMTUTAR || 0,
    }));
  }, [tier2Batch.data]);

  const subeCiroChartData = useMemo(() => {
    const rows = (getResult<KasaRaporuRow>('tier2', 'onlineCiro')?.data || []);
    const grouped = rows.reduce((acc, row) => {
      const magaza = row.MAGAZA || 'Bilinmeyen';
      if (!acc[magaza]) acc[magaza] = 0;
      acc[magaza] += row.TOPLAM || 0;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(grouped)
      .map(([name, value]) => ({ name, value }))
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [tier2Batch.data]);

  const dateFilterOptions = [
    { label: 'Bugün', value: 'today' },
    { label: 'Son 1 Hafta', value: 'last1week' },
    { label: 'Son 1 Ay', value: 'last1month' },
  ];

  return (
    <div style={sectionStyles.chartsSection}>
      <div style={sectionStyles.chartsSectionHeader}>
        <h2 style={sectionStyles.chartsSectionTitle}>Satış Analizleri</h2>
        <Segmented
          options={dateFilterOptions}
          value={dateFilter}
          onChange={(value) => onDateFilterChange(value as DateFilterType)}
          style={{ background: 'var(--color-header-bg)' }}
        />
      </div>

      <div className="charts-grid" style={sectionStyles.chartsGrid}>
        <PieChart
          title="En Çok Satan Ürünler (Miktar)"
          data={topSatisMiktarChartData}
          isLoading={tier2Batch.isLoading}
          valueFormatter={(v) => v.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}
        />
        <PieChart
          title="En Çok Satan Ürünler (Tutar)"
          data={topSatisTutarChartData}
          isLoading={tier2Batch.isLoading}
          valueFormatter={(v) => `₺${v.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`}
        />
        <PieChart
          title="Ciro Şube Dağılımı"
          data={subeCiroChartData}
          isLoading={tier2Batch.isLoading}
          valueFormatter={(v) => `₺${v.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`}
        />
      </div>
    </div>
  );
}

const sectionStyles: Record<string, React.CSSProperties> = {
  chartsSection: {
    marginTop: 8,
  },
  chartsSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    flexWrap: 'wrap' as const,
    gap: 12,
  },
  chartsSectionTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--color-text-primary)',
    margin: 0,
  },
  chartsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 24,
  },
};

const chartStyles: Record<string, React.CSSProperties> = {
  container: {
    background: 'var(--color-card-bg)',
    borderRadius: 12,
    padding: 24,
    boxShadow: '0 1px 3px var(--shadow-color)',
    minHeight: 420,
  },
  title: {
    fontSize: 16,
    fontWeight: 700,
    color: 'var(--color-text-primary)',
    marginBottom: 20,
    textAlign: 'center' as const,
  },
  loading: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 280,
  },
  empty: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 280,
    color: 'var(--color-text-muted)',
    fontSize: 14,
  },
  content: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: 24,
  },
  chartWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '10px 0',
  },
  legend: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 10,
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
    padding: '4px 0',
  },
  legendColor: {
    width: 14,
    height: 14,
    borderRadius: 4,
    flexShrink: 0,
  },
  legendText: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flex: 1,
    minWidth: 0,
  },
  legendLabel: {
    fontSize: 13,
    color: 'var(--color-text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    maxWidth: '55%',
  },
  legendValue: {
    fontSize: 12,
    color: 'var(--color-text-muted)',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap' as const,
  },
};
